import type { ZipBlastCreateBody } from '../../../common/api/zipBlasts';

export type ZipBlastMode = 'single_zip' | 'city_area' | 'zip_list';

export type ZipBlastFormState = {
  mode: ZipBlastMode;
  zip: string;
  city: string;
  state: string;
  zipCodes: string;
  dailyCap: string;
  notes: string;
};

export type ZipBlastFormErrors = Partial<
  Record<'zip' | 'city' | 'state' | 'zipCodes' | 'dailyCap', string>
>;

const ZIP_PATTERN = /^\d{5}$/;
const STATE_PATTERN = /^[A-Za-z]{2}$/;

export function normalizeZipCodes(value: string): string[] {
  const seen = new Set<string>();
  return value
    .split(/[\s,;]+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .filter((part) => {
      if (seen.has(part)) return false;
      seen.add(part);
      return true;
    });
}

export function validateZipBlastForm(state: ZipBlastFormState): ZipBlastFormErrors {
  const errors: ZipBlastFormErrors = {};

  if (state.mode === 'single_zip') {
    if (!ZIP_PATTERN.test(state.zip.trim())) {
      errors.zip = state.zip.trim() ? 'Must be 5 digits' : 'Zip code is required';
    }
  }

  if (state.mode === 'city_area') {
    if (!state.city.trim()) {
      errors.city = 'City is required';
    }
    if (!STATE_PATTERN.test(state.state.trim())) {
      errors.state = state.state.trim() ? 'Use a 2-letter state code' : 'State is required';
    }
  }

  if (state.mode === 'zip_list') {
    const zips = normalizeZipCodes(state.zipCodes);
    const invalid = zips.find((part) => !ZIP_PATTERN.test(part));
    if (zips.length === 0) {
      errors.zipCodes = 'At least one ZIP code is required';
    } else if (invalid) {
      errors.zipCodes = `${invalid} must be 5 digits`;
    }
  }

  if (state.dailyCap.trim()) {
    const dailyCap = Number(state.dailyCap.trim());
    if (!Number.isInteger(dailyCap) || dailyCap < 1 || dailyCap > 10000) {
      errors.dailyCap = 'Daily cap must be a whole number from 1 to 10000';
    }
  }

  return errors;
}

export function buildZipBlastCreateBody(state: ZipBlastFormState): ZipBlastCreateBody {
  const body: ZipBlastCreateBody = {
    notes: state.notes.trim() || undefined,
    target_per_day: state.dailyCap.trim() ? Number(state.dailyCap.trim()) : undefined,
  };

  if (state.mode === 'single_zip') {
    return { ...body, zip_code: state.zip.trim() };
  }

  if (state.mode === 'city_area') {
    return {
      ...body,
      city: state.city.trim(),
      state: state.state.trim().toUpperCase(),
    };
  }

  return {
    ...body,
    zip_codes: normalizeZipCodes(state.zipCodes),
  };
}
